import axios from '@/plugins/axios';
import utils from '@/stores/utils';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    stocks: [],
    stocks_count: 0,
    stocks_loaded: false,
    stocks_loading: false,
    stocks_loading_errors: null,
    stocks_filters: DEFAULT_FILTERS,
    cancelTokenSource: null,
    _storageOptions: options || {},
});


const mutations = {

    updateStocks(state, stocks) {
        state.stocks = stocks;
        state.stocks_loaded = true;
        state.stocks_loading = false;
        state.stocks_loading_errors = null;
    },
    updateStocksCount(state, count) {
        state.stocks_count = count;
    },
    updateStocksLoading(state, loading) {
        state.stocks_loading = loading;
        state.stocks_loaded = false;
    },
    updateStocksLoadingErrors(state, errors) {
        state.stocks_loading_errors = errors;
        state.stocks_loading = false;
        state.stocks_loaded = false;
    },
    updateStocksFilters(state, filters) {
        state.stocks_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.stocks_filters, fieldsToSave);
        }
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};


const actions = {

    async setStockAsDefault({ commit, dispatch, state, rootGetters }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "stock.change_stock"
            ) === -1
        ) {
            return;
        }
        try {
            const url = `/api/stocks/${state.stock.id}/default/`;
            const response = await axios.post(url);
            dispatch('fetchStock');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

    async fetchStocks({ commit, dispatch, state, rootGetters }, params) {
        if (rootGetters['session/current_user_permissions'].indexOf("stock.view_stock") === -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateStocksLoading', true);

        try {
            const response = await axios.get('/api/stocks/', {
                params: state.stocks_filters,
                cancelToken: source.token,
            });
            commit('updateStocks', response.data.results);
            commit('updateStocksCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateStocksLoadingErrors', error.details);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateStocks', []);
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateStocksFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
    },

    async unarchiveStock({ commit, dispatch, state, rootGetters }, params) {
        if (
            rootGetters["session/current_user_permissions"].indexOf(
                "stock.change_stock"
            ) === -1
        ) {
            return Promise.resolve();
        }
        try {
            const url = `/api/stocks/${params.instance.id}/unarchive/`;
            const response = await axios.post(url);
            dispatch('fetchStocks');
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            throw error;
        }
    },

};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});